import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ClinicProvider } from "./contexts/ClinicContext";
import { AppLayout } from "./components/AppLayout";
import Home from "./pages/Home";
import Schedule from "./pages/Schedule";
import Index from "./pages/AppointmentsPage";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import Patients from "./pages/Patients";
import Users from "./pages/Users";
import Dentists from "./pages/Dentists";
import Companies from "./pages/Companies";
import ChatObserver from "./pages/ChatObserver";
import Notifications from "./pages/NotificationCenter";
import ImportAppointments from "./pages/ImportAppointments";
import MedicalRecordDetail from "./pages/MedicalRecordDetail";
import ClinicDetail from "./pages/ClinicDetail";
import ScheduleSetup from "./pages/ScheduleSetup";
import Holidays from "./pages/Holidays";
import EmImplementacao from "./pages/EmImplementacao";
import InsuranceAuthorizationsPage from "./pages/InsuranceAuthorizationsPage";
import TissBillingPage from "./pages/TissBillingPage";
import FollowUps from "./pages/FollowUps";
const queryClient = new QueryClient();
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

// Rota protegida — apenas usuários autenticados
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? (
    <AppLayout>{children}</AppLayout>
  ) : (
    <Navigate to="/login" replace />
  );
}

// Rota restrita por permission — se o usuário não tem a permission exigida, vai pra /home
function PermissionRoute({
  resource,
  action,
  children,
}: {
  resource: string;
  action: string;
  children: React.ReactNode;
}) {
  const { isAuthenticated, can } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!can(resource, action)) return <Navigate to="/home" replace />;
  return <AppLayout>{children}</AppLayout>;
}

const App = () => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ClinicProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            {/* Rotas públicas */}
            <Route path="/login" element={<Login />} />

            {/* /home — qualquer usuário autenticado */}
            <Route path="/home"                     element={<ProtectedRoute><Home /></ProtectedRoute>} />

            {/* Operação diária — quem tem appointments.read */}
            <Route path="/schedule"                 element={<PermissionRoute resource="appointments" action="read"><Schedule /></PermissionRoute>} />
            <Route path="/chat-observer"            element={<PermissionRoute resource="chat_tickets" action="read"><ChatObserver /></PermissionRoute>} />
            <Route path="/notifications"            element={<PermissionRoute resource="notifications" action="read"><Notifications /></PermissionRoute>} />
            <Route path="/appointments"             element={<PermissionRoute resource="appointments" action="read"><Index /></PermissionRoute>} />
            <Route path="/import-appointments"      element={<PermissionRoute resource="appointments" action="import_bulk"><ImportAppointments /></PermissionRoute>} />
            <Route path="/patients"                 element={<PermissionRoute resource="patients" action="read"><Patients /></PermissionRoute>} />
            <Route path="/insurance-authorizations" element={<PermissionRoute resource="insurance_authorizations" action="read"><InsuranceAuthorizationsPage /></PermissionRoute>} />
            <Route path="/tiss-billing"             element={<PermissionRoute resource="insurance_claims" action="read"><TissBillingPage /></PermissionRoute>} />
            <Route path="/follow-ups"               element={<PermissionRoute resource="follow_ups" action="read"><FollowUps /></PermissionRoute>} />
            <Route path="/prontuario/:patientId"    element={<PermissionRoute resource="patients" action="read"><MedicalRecordDetail /></PermissionRoute>} />

            {/* Cadastros administrativos da clínica */}
            <Route path="/dentists"                 element={<PermissionRoute resource="dentists" action="read"><Dentists /></PermissionRoute>} />
            <Route path="/clinics-detail"           element={<PermissionRoute resource="clinics" action="read"><ClinicDetail /></PermissionRoute>} />
            <Route path="/schedule-setup"           element={<PermissionRoute resource="schedule_setups" action="read"><ScheduleSetup /></PermissionRoute>} />
            <Route path="/holidays"                 element={<PermissionRoute resource="holidays" action="read"><Holidays /></PermissionRoute>} />
            <Route path="/em-implementacao"         element={<PermissionRoute resource="clinics" action="update"><EmImplementacao /></PermissionRoute>} />

            {/* Tenancy — companies (super_admin) e users (super_admin + company_admin + clinic_manager) */}
            <Route path="/companies"                element={<PermissionRoute resource="companies" action="read"><Companies /></PermissionRoute>} />
            <Route path="/users"                    element={<PermissionRoute resource="users" action="read"><Users /></PermissionRoute>} />

            {/* Fallbacks */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </ClinicProvider>
      </AuthProvider>
    </QueryClientProvider>
  </GoogleOAuthProvider>
);

export default App;
